import React, { useState } from "react";
import {
  Grid,
  Typography,
  TextField,
  Button,
  Paper,
  Box,
  Alert,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
} from "@mui/material";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import axios from "axios";
import theme from "../components/theme/Theme";
import { backendURL } from "../url";

const StyledPaper = styled(Paper)(({ theme }) => ({
  borderRadius: 16,
  boxShadow: "0 8px 32px rgba(0, 0, 0, 0.1)",
  transition: "transform 0.2s ease-in-out",
  "&:hover": {
    transform: "translateY(-4px)",
  },
}));

const StyledListItemIcon = styled(ListItemIcon)(({ theme }) => ({
  minWidth: "40px",
  color: theme.palette.primary.main,
  "& svg": {
    width: 24,
    height: 24,
    strokeWidth: 2,
  },
}));

const StyledTextField = styled(TextField)(({ theme }) => ({
  "& .MuiOutlinedInput-root": {
    borderRadius: 12,
    transition: "all 0.2s ease-in-out",
    "&:hover": {
      transform: "translateY(-2px)",
    },
    "&.Mui-focused": {
      transform: "translateY(-2px)",
      boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
    },
  },
}));

interface ContactFormData {
  name: string;
  email: string;
  phoneNumber: string;
  subject: string;
  message: string;
}

export const Contact: React.FC = () => {
  // const theme = useTheme();
  const [formData, setFormData] = useState<ContactFormData>({
    name: "",
    email: "",
    phoneNumber: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: "success" | "error" | null;
    message: string;
  }>({ type: null, message: "" });

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: null, message: "" });

    try {
      // const response = axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/enquiry/create-enquiry`, {
      await axios.post(`${backendURL}/api/enquiry/create-enquiry`, {
        ...formData,
      });
      setSubmitStatus({ type: "success", message: "Message sent successfully" });
      setFormData({
        name: "",
        email: "",
        phoneNumber: "",
        subject: "",
        message: "",
      });
    } catch (error) {
      console.error("Error sending form:", error);
      setSubmitStatus({ type: "error", message: "Failed to send message" });
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <Box
      sx={{
        background: "linear-gradient(135deg, #f6f9fc 0%, #ffffff 100%)",
        minHeight: "calc(100vh - 64px)",
        py: 8,
      }}
    >
      <Box
        sx={{
          maxWidth: "80vw",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          mx: "auto",
        }}
      >
        {/* <Container maxWidth="lg"> */}
        <Box sx={{ textAlign: "center", mb: 6 }}>
          <Typography
            variant="h1"
            component="h1"
            gutterBottom
            color={theme.palette.primary.main}
            // sx={{
            //   fontWeight: 800,
            //   background: "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
            //   WebkitBackgroundClip: "text",
            //   WebkitTextFillColor: "transparent",
            //   mb: 2,
            // }}
          >
            Get in Touch
          </Typography>
          <Typography
            variant="h6"
            color="text.secondary"
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            Have questions about our batting cages or training programs? We're
            here to help!
          </Typography>
        </Box>

        <Grid item sx={{ mb: 6, width: "100%" }}>
          <StyledPaper
            elevation={0}
            sx={{ overflow: "hidden", bgcolor: "rgba(255, 255, 255, 0.9)" }}
          >
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2837.9354741701995!2d-63.54674312394101!3d44.65966997107228!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x4b5a23913b46706f%3A0xd64e122cfc4bf15d!2sThe%20Bat%20Cage%20Inc.%20%7C%20Sports%20complex%20in%20Dartmouth!5e0!3m2!1sen!2sca!4v1741199762004!5m2!1sen!2sca"
              width="100%"
              height="300"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              title="Google Maps"
            ></iframe>
          </StyledPaper>
        </Grid>
        <Grid container spacing={6}>
          {/* Contact Information */}
          <Grid item xs={12} md={5}>
            <StyledPaper
              elevation={0}
              sx={{ p: 4, mb: 4, bgcolor: "rgba(255, 255, 255, 0.9)" }}
            >
              <List sx={{ "& .MuiListItem-root": { px: 0 } }}>
                <ListItem>
                  <StyledListItemIcon>
                    <MapPin />
                  </StyledListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6">Location</Typography>}
                    secondary="50A Neptune Crescent Unit 1, Dartmouth, NS B2Y 0B6"
                    secondaryTypographyProps={{
                      sx: { color: "text.secondary" },
                    }}
                  />
                </ListItem>

                <Divider sx={{ my: 3 }} />

                <ListItem>
                  <StyledListItemIcon>
                    <Phone />
                  </StyledListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6">Phone</Typography>}
                    secondary="+1(902) 718-7822"
                    secondaryTypographyProps={{
                      sx: { color: "text.secondary" },
                    }}
                  />
                </ListItem>

                <Divider sx={{ my: 3 }} />

                <ListItem>
                  <StyledListItemIcon>
                    <Mail />
                  </StyledListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6">Email</Typography>}
                    secondary="info@thebatcage.ca"
                    secondaryTypographyProps={{
                      sx: { color: "text.secondary" },
                    }}
                  />
                </ListItem>

                <Divider sx={{ my: 3 }} />

                <ListItem>
                  <StyledListItemIcon>
                    <Clock />
                  </StyledListItemIcon>
                  <ListItemText
                    primary={<Typography variant="h6">Hours</Typography>}
                    secondary={
                      <Box sx={{ color: "text.secondary" }}>
                        Monday - Thursday: 1:00 PM - 10:00 PM
                        <br />
                        Friday: 1:00 PM - 10:00 PM
                        <br />
                        Saturday: 10:00 AM - 10:00 PM
                        <br />
                        Sunday: 10:00 AM - 10:00 PM
                      </Box>
                    }
                  />
                </ListItem>
              </List>
            </StyledPaper>
          </Grid>

          {/* Contact Form */}
          <Grid item xs={12} md={7}>
            <StyledPaper
              elevation={0}
              sx={{ p: 4, bgcolor: "rgba(255, 255, 255, 0.9)" }}
            >
              <Typography
                variant="h4"
                gutterBottom
                sx={{ fontWeight: 700, mb: 4 }}
              >
                Send us a Message
              </Typography>

              {submitStatus.type && (
                <Alert
                  severity={submitStatus.type}
                  sx={{
                    mb: 3,
                    borderRadius: 2,
                    "& .MuiAlert-message": { fontSize: "1rem" },
                  }}
                >
                  {submitStatus.message}
                </Alert>
              )}

              <form onSubmit={handleSubmit}>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <StyledTextField
                      fullWidth
                      label="Email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Phone"
                      name="phoneNumber"
                      required
                      type="number"
                      value={formData.phoneNumber}
                      onChange={handleChange}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      required
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <StyledTextField
                      fullWidth
                      label="Message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      multiline
                      rows={4}
                      variant="outlined"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      disabled={isSubmitting}
                      sx={{
                        minWidth: 200,
                        py: 1.5,
                        px: 4,
                        borderRadius: 3,
                        fontSize: "1.1rem",
                        textTransform: "none",
                        background:
                          "linear-gradient(45deg, #1976d2 30%, #42a5f5 90%)",
                        boxShadow: "0 3px 15px rgba(25, 118, 210, 0.3)",
                        "&:hover": {
                          background:
                            "linear-gradient(45deg, #1565c0 30%, #1976d2 90%)",
                          transform: "translateY(-2px)",
                          boxShadow: "0 6px 20px rgba(25, 118, 210, 0.4)",
                        },
                      }}
                    >
                      {isSubmitting ? "Sending..." : "Send Message"}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </StyledPaper>
          </Grid>
        </Grid>
        {/* </Container> */}
      </Box>
    </Box>
  );
};
